// LIBRERIE/IMPORTS
import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import he from "he";

// HOOKS
import { useGetAgencyDetails } from "../../../hooks/useGetAgencyDetails";

// COMPONENTI
import SubHeaderDefault from "../../Header/subHeader/SubHeaderDefault";
import LoadingIndicator from "../../Loaders/LoadingIndicator";
import NotFound from "../../Error/NotFound";

// CONFIG
import appConfig from "../../../config/appConfig";

// UTILS
import { decodeHTMLEntities } from "../../../utils/textUtils";

interface DatiProps {
    agencyId: number;
}

const Dati: React.FC<DatiProps> = ({ agencyId }) => {
    const { t } = useTranslation();
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(agencyId);

    if (agencyLoading) return <LoadingIndicator />;
    if (agencyError) {
        console.error("Error fetching agency details for Dati page:", agencyError);
        return <NotFound message={t('validation.agencyLoadError') ?? "Errore durante il caricamento dei dati dell'agenzia"} />;
    }
    if (!agency) {
        return <NotFound message={t('common.agencyNotFound') ?? "Agenzia non trovata"} />;
    }

    // --- Preparazione Dati e Testi Tradotti ---
    const agencyName = agency?.agencyName ?? t('common.defaultAgencyName');
    const pageTitle = t('footer.datiSocietariLinkTitle');
    const helmetTitle = t('common.pageTitlePattern', { pageTitle: pageTitle, agencyName: he.decode(`${agencyName}`) });
    
    const labelLegalOffice = t('corporateData.legalHeadOffice');
    const labelVat = t('corporateData.numberVAT');
    const labelTaxCode = t('corporateData.taxCode', {businessRegisterProvince: agency?.businessRegisterProvince || 'Livorno'});
    const labelRea = t('corporateData.rea');
    const labelPec = t('corporateData.certifiedEmail');
    const decodedRegisteredOffice = decodeHTMLEntities(agency?.registeredOffice);
    const labelSocialCapital = t('corporateData.socialCapital');

    // --- Preparazione Dati da visualizzare (API + Config)
    const showRegisteredOffice = appConfig.ui.corporateData.showRegisteredOffice && agency.registeredOffice;
    const showVat = appConfig.ui.corporateData.showVat && agency.vat;
    const showFiscalCode = appConfig.ui.corporateData.showFiscalCode && agency.codicefiscale;
    const showRea = appConfig.ui.corporateData.showRea && agency.rea;
    const showPec = appConfig.ui.corporateData.showPec && agency.pec;
    const showSocialCapital = appConfig.ui.corporateData.showSocialCapital && agency.socialCapital && !agency.soleProprietorship;

    return (
        <>
            <Helmet>
                <title>{he.decode(`${helmetTitle}`)}</title>
            </Helmet>
            <SubHeaderDefault title={pageTitle ?? ''} isSeo={true}/>

            <section id="container-static" className="container py-4">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        <div className="card shadow border-0 rounded-4">
                        <div className="card-body p-4 p-md-5">
                                <h2 className="card-title text-center fw-bold h4 mb-4">{agency?.ragioneSociale}</h2>
                                <hr className="mb-4"/>

                                <ul className="list-group list-group-flush">

                                    {/* Sede Legale */}
                                    {showRegisteredOffice &&
                                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">                                        <span className="fw-bold">{labelLegalOffice}:</span>
                                            <span className="text-muted text-right">
                                                {decodedRegisteredOffice}
                                            </span>
                                        </li>
                                    }

                                    {/* P.IVA */}
                                    {showVat &&
                                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                                            <span className="fw-bold">{labelVat}:</span>
                                            <span className="text-muted">{agency?.vat || t('common.notAvailable')}</span>
                                        </li>
                                    }

                                    {/* Codice Fiscale */}
                                    {showFiscalCode &&
                                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                                            <span className="fw-bold">{labelTaxCode}:</span>
                                            <span className="text-muted">{agency?.codicefiscale || t('common.notAvailable')}</span>
                                        </li>
                                    }

                                    {/* REA */}
                                    {showRea &&
                                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                                            <span className="fw-bold">{labelRea}:</span>
                                            <span className="text-muted">{agency?.rea || t('common.notAvailable')}</span>
                                        </li>
                                    }

                                    {/* PEC */}
                                    {showPec &&
                                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                                            <span className="fw-bold">{labelPec}:</span>
                                            {agency?.pec ? (
                                                <a href={`mailto:${agency.pec}`} className="text-muted">{agency.pec}</a>
                                            ) : (
                                                <span className="text-muted">{t('common.notAvailable')}</span>
                                            )}
                                        </li>
                                    }

                                    {/* Capitale Sociale */}
                                    {showSocialCapital &&
                                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                                            <span className="fw-bold">{labelSocialCapital}:</span>
                                            <span className="text-muted">{agency?.socialCapital || t('common.notAvailable')}</span>
                                        </li>
                                    }

                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Dati;